<template>
  <div class="d-flex flex-column ga-8">
    <v-row>
      <v-col cols="6">
        <v-select
          v-model="selectedYear"
          :items="years"
          density="compact"
          hide-details
          variant="solo"
        />
      </v-col>

      <v-col cols="6">
        <v-select
          v-model="selectedWeek"
          :items="weekOptions"
          density="compact"
          hide-details
          variant="solo"
        />
      </v-col>
    </v-row>

    <div class="">
      <div class="d-flex align-center justify-space-between">
        <p>{{ $t("dag") }}</p>
        <p>{{ $t("gebruikt-totaal") }}</p>
      </div>

      <div v-if="loading" class="d-flex flex-column ga-4 mt-6">
        <div v-for="i in 4" :key="i" class="">
          <v-skeleton-loader type="list-item" />
        </div>
      </div>

      <div v-else class="d-flex flex-column ga-4 mt-6">
        <div v-for="(credit, index) in credits" :key="index" class="">
          <div class="d-flex align-center justify-space-between">
            <p class="text-bold text-capitalize">
              {{ formatDay(credit.date) }}
            </p>
            <p>{{ credit.credits }}/{{ max }}</p>
          </div>

          <v-progress-linear
            class="rounded-sm"
            :max="max"
            :model-value="credit.credits"
            color="var(--company-primary)"
          ></v-progress-linear>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CreditsService from "../../../services/CreditsService";

export default {
  data() {
    return {
      selectedYear: null,
      selectedWeek: null,

      loading: true,
      credits: [],
    };
  },

  props: {
    max: {
      type: Number,
      required: true,
    },
  },

  mounted() {
    const today = new Date();
    this.selectedYear = today.getFullYear();
    this.selectedWeek = this.weekOptions[0];

    this.getCredits();
  },

  computed: {
    years() {
      const currentYear = new Date().getFullYear();

      // make an array of this year and the next 3 years
      const years = [];
      for (let i = 0; i < 4; i++) {
        years.push(currentYear + i);
      }
      return years;
    },

    weekOptions() {
      const currentYear = new Date().getFullYear();
      const currentWeek = this.getWeekNumber(new Date());

      // If selected year is current year, return current week + remaining weeks
      if (this.selectedYear === currentYear) {
        const weeks = [];
        const totalWeeksInYear = this.getTotalWeeksInYear(currentYear);

        for (let i = currentWeek; i <= totalWeeksInYear; i++) {
          weeks.push(this.$t("week-weeknumber", [i]));
        }
        return weeks;
      }

      // For other years, return all weeks
      const weeks = [];
      const totalWeeksInYear = this.getTotalWeeksInYear(this.selectedYear);

      for (let i = 1; i <= totalWeeksInYear; i++) {
        weeks.push(this.$t("week-weeknumber", [i]));
      }
      return weeks;
    },

    days() {
      if (!this.selectedYear || !this.selectedWeek) return [];

      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentWeek = this.getWeekNumber(currentDate);

      // Extract week number from selectedWeek string ("Week X" -> X)
      const weekNumber = parseInt(this.selectedWeek.split(" ")[1]);
      if (isNaN(weekNumber)) return [];

      // Get all days in the selected week
      const daysInWeek = this.getDaysInWeek(this.selectedYear, weekNumber);

      // If selected week is current week in current year, filter to current and future days
      if (this.selectedYear === currentYear && weekNumber === currentWeek) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        return daysInWeek.filter((day) => {
          return day.date >= today;
        });
      }

      // For future weeks, return all days
      return daysInWeek;
    },
  },

  methods: {
    async getCredits() {
      try {
        this.loading = true;
        const weekNumber = parseInt(this.selectedWeek.split(" ")[1]);
        const response = await CreditsService.getDailyCredits(
          this.selectedYear,
          weekNumber
        );

        if (response) {
          this.credits = response.data.data;
          this.loading = false;
        }
      } catch (error) {
        console.error("Error getting credits", error);
        this.$toast.error(
          "Er is iets fout gegaan bij het ophalen van de credits"
        );
      }
    },

    formatDay(dateStr) {
      const date = new Date(dateStr);

      // Pick locale based on i18n setting
      const locale = this.$i18n?.locale === "nl" ? "nl-NL" : "en-US";

      // Weekday and month in the right language
      let dayName = date.toLocaleDateString(locale, { weekday: "long" });
      const day = date.getDate();
      const month = date.toLocaleDateString(locale, { month: "short" });

      // Optional: capitalize first letter (Dutch weekdays are usually lowercased)
      dayName = dayName.charAt(0).toUpperCase() + dayName.slice(1);

      return `${dayName} (${day} ${month})`;
    },

    getWeekNumber(date) {
      const d = new Date(
        Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
      );
      const dayNum = d.getUTCDay() || 7;
      d.setUTCDate(d.getUTCDate() + 4 - dayNum);
      const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
      return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
    },

    getTotalWeeksInYear(year) {
      // Check if the year has 53 weeks
      const dec31 = new Date(year, 11, 31);
      const lastWeek = this.getWeekNumber(dec31);
      return lastWeek === 1 ? 52 : lastWeek;
    },

    getDateOfISOWeek(weekNumber, year) {
      // Get January 4th of the year (always in week 1)
      const jan4 = new Date(year, 0, 4);

      // Calculate the Monday of week 1
      const dayOfWeek = jan4.getDay() || 7; // Make Sunday = 7
      const week1Monday = new Date(jan4);
      week1Monday.setDate(jan4.getDate() - dayOfWeek + 1);

      // Calculate the Monday of the target week
      const targetMonday = new Date(week1Monday);
      targetMonday.setDate(week1Monday.getDate() + (weekNumber - 1) * 7);

      return targetMonday;
    },

    getDaysInWeek(year, weekNumber) {
      const days = [];
      const monday = this.getDateOfISOWeek(weekNumber, year);

      // Generate all 7 days of the week (Monday to Sunday)
      for (let i = 0; i < 7; i++) {
        const day = new Date(monday);
        day.setDate(monday.getDate() + i);

        const dayName = day.toLocaleDateString(this.$i18n.locale, {
          weekday: "long",
        });
        const dateStr = day.toLocaleDateString(this.$i18n.locale, {
          day: "numeric",
          month: "short",
        });

        days.push({
          name: `${dayName} (${dateStr})`,
          date: new Date(day),
        });
      }

      return days;
    },
  },

  watch: {
    selectedYear() {
      this.selectedWeek = this.weekOptions[0];
      this.getCredits();
    },
    selectedWeek() {
      this.getCredits();
    },
  },
};
</script>
